import React, { useState, useEffect } from 'react';
import { X, Crown, CheckCircle, Sparkles, FileText, Download, Eye, TrendingUp, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const ResumeUpgradePopup = ({ isOpen, onClose, onUpgrade }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!isOpen) return null;

  const benefits = [
    { icon: FileText, title: 'ATS-Optimized PDF', desc: 'Download recruiter-friendly resume' },
    { icon: Eye, title: 'Company Views', desc: 'See who viewed your profile' },
    { icon: TrendingUp, title: 'Resume Score', desc: 'Get detailed analytics & tips' },
    { icon: Shield, title: 'Verified Badge', desc: 'Stand out with verification' },
    { icon: Sparkles, title: 'AI Enhancement', desc: 'AI-powered content suggestions' },
    { icon: Zap, title: 'Priority Visibility', desc: 'Higher ranking in searches' },
  ];

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 rounded-2xl border border-purple-500/30 shadow-2xl transform transition-all duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
          data-testid="close-popup"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="relative p-6 pb-4 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Crown className="text-white" size={32} />
            </div>
          </div>
          
          <div className="mt-6">
            <h2 className="text-2xl font-bold text-white mb-2">Upgrade Your Resume</h2>
            <p className="text-gray-400">Enhance your resume with a professionally verified badge for this role, increasing your chances of selection.</p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-3 p-3 bg-white/5 rounded-xl border border-white/10"
              >
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <benefit.icon size={18} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{benefit.title}</p>
                  <p className="text-gray-500 text-xs">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price Section */}
        <div className="px-6 py-4 bg-gradient-to-r from-purple-500/10 to-amber-500/10 border-t border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">One-time upgrade</p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">₹99</span>
                <span className="text-gray-500 line-through text-sm">₹499</span>
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">80% OFF</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-amber-400 text-sm font-medium">Limited Time Offer</p>
              <p className="text-gray-500 text-xs">Lifetime access</p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="p-6 space-y-3">
          <button
            onClick={onUpgrade}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30"
            data-testid="upgrade-now-btn"
          >
            <Crown size={20} />
            Upgrade Now - ₹99
          </button>
          
          <Link
            to="/codearena/resume-upgrade"
            className="block w-full py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all text-center"
            onClick={handleClose}
          >
            Learn More About Premium
          </Link>
          
          <button
            onClick={handleClose}
            className="w-full text-gray-500 text-sm hover:text-gray-400 transition-colors"
          >
            Maybe Later
          </button>
        </div>

        {/* Trust Indicators */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <CheckCircle size={14} className="text-green-500" />
              Secure Payment
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle size={14} className="text-green-500" />
              Instant Access
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle size={14} className="text-green-500" />
              10,000+ Users
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeUpgradePopup;
